import React, {useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../layout/components/CustomButton";
import { RootState } from "../../../../redux/store";
import { Box, CircularProgress, styled } from "@mui/material";
import { setNextButtonClick } from "../../../../redux/slice/apartmentRental/apartmentRentalSlice";
import { setHasSelectedLocation } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { BookingCreationStep } from "../../roomRental/bookingCreationStep";
import { useBookingFlowService } from "../useBookingFlowService";
import { useDirectBookingFlow } from "../hooks/useDirectBookingFlow";
import { COVERED_BOOK_NOW_KEY, summaryCtaLabelKey } from "../hooks/useSummaryCtaLabelKey";
import { useIsCoveredBooking } from "../hooks/useCoveredBooking";

const NextButton = styled(CustomButton)(({ theme }) => ({
  width: "100%",
  borderRadius: theme.shape.borderRadius,
  border: "none",
  "&:hover": {
    backgroundColor: theme.palette.secondary.contrastText,
    color: theme.palette.button.text,
    border: "none",
  },
  "&:focus": {
    outline: "none",
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.button.text,
  },
  textTransform: "none",
}));

interface BookLocationNavigateNextActionProps {
  handleCall?: () => Promise<any>;
}

/**
 * The per-step label, keyed on the flow the page runs (request vs. paid) —
 * and first on coverage: a covered booking books at once without payment, so
 * it reads "Jetzt buchen", then "Fortfahren" (no "Zur Kasse", no
 * "Reservieren"), whatever the configured mode.
 */
const nextButtonLabelKey = (step: number, isRequestFlow: boolean, covered: boolean): string => {
  // The summary CTA states whether a payment step follows — one rule, shared
  // with both room-family pages (`summaryCtaLabelKey`). It used to hardcode
  // the apartment key, which promises "Weiter zur Zahlung" in a flow that
  // takes no payment.
  if (step === BookingCreationStep.bookingSummary) {
    return summaryCtaLabelKey(!isRequestFlow, covered);
  }
  if (step === 2) {
    if (covered) return COVERED_BOOK_NOW_KEY;
    return isRequestFlow
      ? "book_location.venue_reservation_overview.book_now"
      : "book_location.apartment_overview.book_now";
  }
  if (step === 3) {
    return isRequestFlow || covered
      ? "book_location.venue_reservation_overview.continue"
      : "book_location.confirmation.order_now";
  }
  return "book_location.next";
};

const ApartmentRentalNavigateNextAction: React.FC<
  BookLocationNavigateNextActionProps
> = ({ handleCall }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const step = useSelector((state: RootState) => state.apartment.step);
  const [loading, setLoading] = useState(false);
  const { nextEnabled } = useSelector(
    (state: RootState) => state.bookingCreation
  );

  // Which flow this is comes from the flow, not from per-tab storage —
  // see `useBookingFlowService` (QA #2764 E2).
  const currentService = useBookingFlowService();

  // Wire 2: the labels follow the flow the page runs, not the service string.
  // Both room-family pages can run either flow now — the configured value of
  // THIS flow's use case decides (legacy default: the description's
  // `directBookingDefault`, read inside `useDirectBookingFlow`).
  const isRequestFlow = !useDirectBookingFlow(currentService);

  useEffect(() => {
    if (step !== undefined) {
      if (step !== BookingCreationStep.personalData) {
        dispatch(setHasSelectedLocation(true));
      }
    }
  }, [dispatch, step]);

  const handleNextClick = async () => {
    if (step === BookingCreationStep.bookingSummary && handleCall) {
      setLoading(true);
      try {
        await handleCall();
      } catch {
        // The handler has already told the customer (`updateReservation`
        // enqueues an error notification). Stay on the summary so they can try
        // again, rather than advancing to a payment step built on a failed call.
        return;
      } finally {
        // Without this a rejected call left the spinner turning forever.
        setLoading(false);
      }
      dispatch(setNextButtonClick());
    } else {
      dispatch(setNextButtonClick());
    };
    if (step === BookingCreationStep.bookingSummary) {
      dispatch(setHasSelectedLocation(false));
    }
  };

  const isCovered = useIsCoveredBooking();
  const buttonText = t(nextButtonLabelKey(step, isRequestFlow, isCovered));

  return (
    <Box>
      <NextButton
        key={`${true}`} // Fixed key (removed unnecessary negation)
        variant={true ? "outlined" : "contained"}
        disabled={!nextEnabled}
        onClick={handleNextClick}
        isButtonDark={true}
      >
        {loading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={18} color="inherit" />
            {buttonText}
          </Box>
        ) : (
          buttonText
        )}
      </NextButton>
    </Box>
  );
};

export default ApartmentRentalNavigateNextAction;
